import { Button, Loader, Title } from "@/shared";
import { MouseEvent, useEffect } from "react";
import styles from "./GenerateAnalyticReportForm.module.scss";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "@/shared/hooks";
import { isObjectHasValues } from "@/shared/helpers/isObjectHasValues";
import { DatePicker, Select } from "antd";
import clsx from "clsx";
import type { RangePickerProps } from "antd/es/date-picker";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/ru";
import locale from "antd/es/date-picker/locale/ru_RU";
import { clearError } from "@/entities/marginality/slice";
import {
  fetchFilters,
  generateOrderAnalytics,
  setSelectedAnalyticsFilters,
  setSelectedDates,
} from "@/entities/dmsanalytic/slice";
import {
  GenerateAnalyticReportBodyType,
  StatusFiltersType,
} from "@/entities/dmsanalytic/type";
import { useDrawer } from "@/app/providers/DrawerProvider/useDrawer";
const { RangePicker } = DatePicker;

const REPORT_DATE_LABEL_FMT = "DD.MM.YYYY";

function getInitialPeriodFromStore(selectedDates: string[]): [Dayjs, Dayjs] {
  if (selectedDates.length === 2) {
    const start = dayjs(selectedDates[0], REPORT_DATE_LABEL_FMT, true);
    const end = dayjs(selectedDates[1], REPORT_DATE_LABEL_FMT, true);
    if (start.isValid() && end.isValid()) {
      return [start.startOf("day"), end.endOf("day")];
    }
  }
  return [dayjs().subtract(1, "month").startOf("day"), dayjs().endOf("day")];
}
type SelectOptionsType = {
  value: string;
  label: string;
};
type FormValues = {
  /** Кортеж из двух слотов; null на этапе выбора диапазона в календаре */
  period: [Dayjs | null, Dayjs | null];
  statusFilters: string[]; // Изменено на string[], так как Select возвращает строки
};
interface GenerateAnalyticReportFormProps {}
export const GenerateAnalyticReportForm: React.FC<
  GenerateAnalyticReportFormProps
> = () => {
  const dispatch = useAppDispatch();
  const { error, isMiniLoading } = useAppSelector((state) => state.marginality);
  const selectedDatesFromReport = useAppSelector(
    (state) => state.dmsanalytic.selectedDates,
  );

  const { closeDrawer } = useDrawer();

  const methods = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      period: getInitialPeriodFromStore(selectedDatesFromReport),
      statusFilters: [],
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = methods;

  const resetForm = () => {
    closeDrawer();
    methods.reset();
  };

  const resetClickHandler = async (e: MouseEvent) => {
    e.preventDefault();
    dispatch(setSelectedAnalyticsFilters({}));
    dispatch(setSelectedDates([]));
    resetForm();
  };

  const orderTypeOptions: SelectOptionsType[] = [
    { value: "confirmed", label: "Подтвержденные позиции" },
    { value: "unconfirmed", label: "Неподтвержденные позиции" },
    { value: "shipped", label: "Отгруженные" },
    { value: "unshipped", label: "Неотгруженные" },
  ];
  const disabledRangeDate: RangePickerProps["disabledDate"] = (current) => {
    const today = dayjs();
    const maxEndDate = today.add(1, "year").subtract(1, "day");
    const minStartDate = today.subtract(365, "day").add(1, "day");
    if (current && current > maxEndDate) return true;
    if (current && current < minStartDate) return true;
    if (current && current > today) return true;
    return false;
  };

  /** Не менее «месяца»: целый diff month отсекает 30 дней (15.01–14.02); порог 28 дней совпадает с ожиданием «~4 недели». */
  const isPeriodAtLeastOneMonth = (start: Dayjs, end: Dayjs) => {
    const diffInDays = end.diff(start, "day");
    const diffInMonthsFloat = end.diff(start, "month", true);
    return diffInMonthsFloat >= 1 || diffInDays >= 28;
  };
  const handleStatusFilterChange = (value: string[]) => {
    if (value.length === 0) {
      methods.setValue("statusFilters", []);
    } else {
      methods.setValue("statusFilters", value);
    }
  };

  const onSubmit = async (data: FormValues) => {
    const [from, to] = data.period;
    if (!from || !to) return;
    const statusFilters: StatusFiltersType[] = data.statusFilters
      .filter((filter) => filter !== "all")
      .map((filter) => filter as StatusFiltersType);
    const requestData: GenerateAnalyticReportBodyType = {
      dateFrom: from.format("YYYY-MM-DD"),
      dateTo: to.format("YYYY-MM-DD"),
      statusFilters: statusFilters.length > 0 ? statusFilters : [],
    };
    dispatch(
      setSelectedDates([from.format("DD.MM.YYYY"), to.format("DD.MM.YYYY")]),
    );
    await dispatch(generateOrderAnalytics(requestData));
    await dispatch(fetchFilters({ filters: [] }));
    closeDrawer();
    resetForm();
  };

  useEffect(() => {
    dispatch(clearError());
  }, []);

  if (isMiniLoading) {
    return <Loader isMain={false} />;
  }
  return (
    <>
      <Title text="Сформировать отчет" type="default" />
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className={styles.formWrapper}>
          {/* Period Range Picker */}
          <div className={clsx(styles.withoutBefore)}>
            <Controller
              name="period"
              control={control}
              rules={{
                required: "Выберите период",
                validate: (value) => {
                  if (!value || !value[0] || !value[1])
                    return "Выберите период";
                  const diffInDays = value[1].diff(value[0], "day");
                  if (diffInDays > 365) {
                    return "Период не должен превышать 365 дней";
                  }
                  if (diffInDays < 0) {
                    return "Начальная дата должна быть раньше конечной";
                  }
                  if (!isPeriodAtLeastOneMonth(value[0], value[1])) {
                    return "Период должен быть не менее месяца";
                  }
                  return true;
                },
              }}
              render={({ field, fieldState }) => (
                <>
                  <RangePicker
                    locale={locale}
                    value={field.value}
                    onCalendarChange={(dates) => {
                      field.onChange([dates[0], dates[1]]);
                    }}
                    onChange={(dates) => {
                      if (dates) {
                        field.onChange([dates[0], dates[1]]);
                      }
                    }}
                    disabledDate={disabledRangeDate}
                    format="DD.MM.YYYY"
                    style={{ width: "100%" }}
                    status={fieldState.error ? "error" : ""}
                    allowClear={false}
                  />
                  {fieldState.error && (
                    <div className={styles.errorMessage}>
                      {fieldState.error.message}
                    </div>
                  )}
                </>
              )}
            />
          </div>
          {/* Ordertype MultiSelect */}
          <div className={clsx(styles.withoutBefore)}>
            <Controller
              name="statusFilters"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <Select
                    mode="multiple"
                    options={orderTypeOptions}
                    placeholder="Исполнение заказа"
                    onChange={handleStatusFilterChange}
                    value={field.value}
                    status={fieldState.error ? "error" : ""}
                  />
                  {fieldState.error && (
                    <div className={styles.errorMessage}>
                      {fieldState.error.message}
                    </div>
                  )}
                </>
              )}
            />
          </div>
          {error && (
            <div>
              <span className={styles.errorText}>
                Период не должен превышать 365 дней
              </span>
            </div>
          )}
          <div className={styles.formButtons}>
            <Button
              onClick={resetClickHandler}
              name="Закрыть"
              type="button"
              buttontStyle="cancelBtn"
            />
            <Button
              type="submit"
              name="Сформировать отчет"
              disabled={isObjectHasValues(errors)}
              buttontStyle="default"
            />
          </div>
        </form>
      </FormProvider>
    </>
  );
};
